import React, { useEffect, useRef, useState } from "react";
import { AD } from "./adTokens";
import { Box, Card, Typography, makeStyles } from "@material-ui/core";
import { alpha, darken } from "@material-ui/core/styles";
import { AlignLeft, Link2, MapPin, Text, Type, User } from "lucide-react";
import AD_TYPE_ICONS from "./adTypeIcons";
import NewAdView from "./NewAdView";
import { DASH, currencySymbol, fmtAge, geoSummaryParts } from "./adVariantFormat";
import { SummaryText, TargetRow } from "./adVariantCardParts";

const CTA_COLOR = "#42B864";

const SUMMARY_LINES = 2;
const ICON_TILE = 17;
const AD_PREVIEW_W = 300;
const CAPTION_LINES = 3;

const WHATSAPP_TYPES = ["ctwa", "whatsapp_status"];

const TYPE_LABEL = {
  ctwa: "Click to WhatsApp",
  whatsapp_status: "WhatsApp Status",
  website_to_whatsapp: "Website",
  lead_form: "Instant form",
};

const destinationOf = (values, variant) => {
  if (WHATSAPP_TYPES.includes(variant.adType)) return "WhatsApp";
  if (variant.adType === "lead_form") return "Lead form";
  return String(values.destination || "").replace(/^https?:\/\//, "");
};

const COPY_ROWS = [
  {
    key: "destination",
    label: "Destination",
    icon: <Link2 size={ICON_TILE} />,
    display: destinationOf,
  },
  {
    key: "primaryText",
    label: "Primary text",
    icon: <AlignLeft size={ICON_TILE} />,
    display: (values) => values.primaryText,
  },
  {
    key: "headline",
    label: "Headline",
    icon: <Type size={ICON_TILE} />,
    display: (values) => values.headline,
  },
  {
    key: "headlineDescription",
    label: "Description",
    icon: <Text size={ICON_TILE} />,
    display: (values) => values.headlineDescription,
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    boxShadow: AD.shadowCard,
    borderRadius: AD.radiusLg,
    overflow: "hidden",
    background: AD.surface,
    position: "relative",
  },
  media: {
    position: "relative",
    width: "100%",
    height: 300,
    overflow: "hidden",
    flexShrink: 0,
    [theme.breakpoints.down(1400)]: { height: 380 },
    [theme.breakpoints.down(1050)]: { height: 420 },
  },
  ctaOption: {
    fontSize: 13,
    color: AD.text,
  },
  previewSlot: {
    width: "100%",
    marginBottom: 4,
    overflow: "hidden",
    flexShrink: 0,
    display: "flex",
    justifyContent: "center",
    "& .MuiCard-root": {
      boxShadow: "none",
      borderRadius: 0,
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 4,
    padding: "2px 2px 6px 8px",
    background: AD.surface,
    borderBottom: `1px solid ${AD.border}`,
    flexShrink: 0,
  },
  typeLabelBox: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    minWidth: 0,
  },
  typeLabelText: {
    fontSize: 11,
    fontWeight: 700,
    color: AD.text,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },

  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
  },

  tile: {
    width: 30,
    height: 30,
    borderRadius: AD.radiusLg,
    color: CTA_COLOR,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  section: { padding: "18px 14px 0" },

  row: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    "& + &": { marginTop: 13 },
  },
  rowBody: { minWidth: 0, flex: 1, paddingTop: 1 },
  rowEdit: {
    alignSelf: "flex-start",
    width: 22,
    height: 22,
    padding: 0,
    flexShrink: 0,
    color: AD.textMuted,
    "&:hover": { color: CTA_COLOR, background: alpha(CTA_COLOR, 0.12) },
  },
  rowLabel: {
    fontSize: 12.5,
    fontWeight: 700,
    color: AD.text,
    lineHeight: 1.3,
    marginBottom: 3,
  },
  rowValue: { fontSize: 13, color: AD.textMuted, lineHeight: 1.45 },
  rowCounter: {
    margin: "2px 0 0",
    fontSize: 10,
    color: AD.textHint,
    textAlign: "right",
  },
  rowValueClamp: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: SUMMARY_LINES,
    overflow: "hidden",
  },
  moreLink: {
    display: "inline-block",
    marginTop: 4,
    fontSize: 11,
    fontWeight: 700,
    color: CTA_COLOR,
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": { textDecoration: "underline" },
  },

  footer: { padding: "11px 14px 14px" },
  statRow: {
    display: "flex",
    alignItems: "stretch",
    gap: 8,
  },
  runSlot: {
    flex: "0.72 1 0",
    minWidth: 0,
    display: "flex",
    alignItems: "flex-end",
  },
  runWrap: {
    display: "flex",
    width: "100%",
  },

  runButton: {
    width: "100%",
    height: AD.controlHeight,
    background: CTA_COLOR,
    color: AD.surface,
    borderRadius: AD.radiusLg,
    textTransform: "none",
    fontWeight: 700,
    fontSize: 12.5,
    padding: "9px 10px",
    whiteSpace: "nowrap",
    "&:hover": { background: darken(CTA_COLOR, 0.15) },
    "&.Mui-disabled": {
      background: AD.surfaceSunken,
      color: AD.textMuted,
      boxShadow: `inset 0 0 0 1px ${AD.border}`,
    },
  },

  runButtonBusy: {
    "&.Mui-disabled": {
      background: CTA_COLOR,
      color: AD.surface,
      boxShadow: "none",
      opacity: 0.75,
    },
  },

  "@keyframes adRunDot": {
    "0%, 70%, 100%": { opacity: 0 },
    "35%": { opacity: 1 },
  },
  runDots: {
    display: "inline-block",
    width: "1.2em",
    textAlign: "left",
  },
  runDot: {
    animation: "$adRunDot 1.4s infinite both",
  },
  runDotTwo: { animationDelay: "0.2s" },
  runDotThree: { animationDelay: "0.4s" },

  failedMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: `linear-gradient(150deg, ${AD.surfaceSubtle} 0%, ${AD.borderSubtle} 55%, ${AD.surfaceSubtle} 100%)`,
    height: 200,
    [theme.breakpoints.down(1400)]: { height: 220 },
    [theme.breakpoints.down(1050)]: { height: 240 },
  },
  failedArt: {
    width: 88,
    height: 88,
    borderRadius: "50%",
    background: AD.surface,
    boxShadow: AD.shadowCard,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  failedTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: AD.text,
    lineHeight: 1.3,
  },
  failedReason: {
    marginTop: 5,
    fontSize: 12.5,
    color: AD.textMuted,
    lineHeight: 1.45,
  },
}));

export default function AdVariantCardReadOnly({ variant = {}, live = null }) {
  const classes = useStyles();
  const previewRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [geoOpen, setGeoOpen] = useState(false);

  useEffect(() => {
    const el = previewRef.current;
    if (!el) return undefined;

    const measure = () => {
      const width = el.clientWidth;
      if (width) setPreviewScale(Math.min(1, width / AD_PREVIEW_W));
    };
    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const imageUrl = variant.imageUrl || "";
  const videoUrl = variant.videoUrl || "";
  const primaryText = variant.adDescription || variant.creativeSubheadline || "";
  const headline = variant.headline || "";
  const headlineDescription = variant.headlineDescription || "";
  const websiteUrl = variant.websiteUrl || "";
  const typeLabel = TYPE_LABEL[variant.adType] || variant.adType || "Ad";

  const copyValues = {
    destination: websiteUrl,
    primaryText,
    headline,
    headlineDescription,
  };

  const geo = geoSummaryParts(variant);
  const age = fmtAge({ minAge: variant.minAge, maxAge: variant.maxAge });
  const symbol = currencySymbol(variant.currency);
  const isLifetime = variant.budgetType === "lifetime_budget";
  const budget = Number(isLifetime ? variant.lifetimeBudget : variant.dailyBudget) || 0;

  const adViewPayload = {
    adCaption: primaryText,
    adHeadline: headline,
    adHeadlineDescription: headlineDescription,
    mediaType: videoUrl ? "VIDEO" : "IMAGE",
    imageFile: imageUrl ? { publicUrl: imageUrl } : null,
    videoFile: videoUrl ? { publicUrl: videoUrl } : null,
    thumbnailUrl: imageUrl || null,
    ctaButton: variant.callToAction || variant.ctaButton || "WHATSAPP_MESSAGE",
    adType: { value: variant.adType || "click_to_whatsapp" },
    pageDetails: {
      picture: "",
      pageName:
        (variant.product && variant.product.name) || variant.pageName || "Your Business",
      whatsappNumber: "",
    },
    previewLink: null,
  };

  return (
    <Card className={classes.root} data-testid="ad-template-card">
      <Box className={classes.header}>
        <Box className={classes.typeLabelBox}>
          {AD_TYPE_ICONS[variant.adType]}
          <span className={classes.typeLabelText}>{typeLabel}</span>
        </Box>
      </Box>

      <Box className={classes.previewSlot} ref={previewRef}>
        <Box style={{ zoom: previewScale }}>
          <NewAdView
            payload={adViewPayload}
            websiteUrl={websiteUrl}
            isFromFlowKeyword
            hideInAppBrowserNote
            hidePageHeader
            hideDestination
            footerBg={AD.surface}
            captionClamp={CAPTION_LINES}
          />
        </Box>
      </Box>

      <Box className={classes.body}>
        <Box className={classes.section}>
          <TargetRow
            classes={classes}
            icon={<MapPin size={ICON_TILE} />}
            label="Locations"
          >
            {geo.labels.length ? (
              <SummaryText
                classes={classes}
                labels={geo.labels}
                open={geoOpen}
                onToggle={() => setGeoOpen((open) => !open)}
              />
            ) : (
              <Typography className={classes.rowValue}>{DASH}</Typography>
            )}
          </TargetRow>

          <TargetRow classes={classes} icon={<User size={ICON_TILE} />} label="Age">
            <Typography className={classes.rowValue}>
              {age && age !== DASH ? `${age} years` : DASH}
            </Typography>
          </TargetRow>

          {COPY_ROWS.map((row) => (
            <TargetRow key={row.key} classes={classes} icon={row.icon} label={row.label}>
              <Typography className={`${classes.rowValue} ${classes.rowValueClamp}`}>
                {row.display(copyValues, variant) || DASH}
              </Typography>
            </TargetRow>
          ))}

          <TargetRow classes={classes} icon={<Type size={ICON_TILE} />} label="Budget">
            <Typography className={classes.rowValue}>
              {budget
                ? `${symbol}${budget.toLocaleString("en-IN")} ${isLifetime ? "total" : "/ day"}`
                : DASH}
            </Typography>
          </TargetRow>
        </Box>
      </Box>
    </Card>
  );
}
